<template>
  <v-dialog
    v-model="openDialog"
    max-width="360"
    persistent
    content-class="floor-plan-element-dialog"
  >
    <div class="content-container">
      <div class="card-header">
        <p class="ticket-name">{{ workflowLabel(orderItem.name) }}</p>
      </div>

      <p v-if="helperText" class="helper-text">{{ workflowLabel(helperText) }}</p>

      <div v-if="orderItem.description" class="description-container">
        <p :class="['description', { 'description--expanded': descriptionExpanded }]">
          {{ workflowLabel(orderItem.description) }}
        </p>
        <button
          v-if="showDescriptionToggle"
          type="button"
          class="description-toggle"
          @click="toggleDescription"
        >
          <span>{{ workflowLabel(descriptionExpanded ? workflowCustomerLabel('readLess', {}, $t('readLess')) : workflowCustomerLabel('readMore', {}, $t('readMore'))) }}</span>
          <v-icon :size="16" color="#333333">
            {{ descriptionExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
          </v-icon>
        </button>
      </div>

      <div
        :class="[
          'ticket-container',
          {
            'ticket-container--shared': isSharedTable,
            'ticket-container--shared-single':
              isSharedTable && sharedAllowedQuantities.length === 1,
          },
        ]"
      >
        <p class="ticket-container__price">{{ workflowLabel(formattedPrice) }}</p>
        <div
          v-if="isSharedTable"
          :class="[
            'shared-table-packages',
            { 'shared-table-packages--single': sharedAllowedQuantities.length === 1 },
          ]"
          role="radiogroup"
          :aria-label="workflowLabel(workflowCustomerLabel('sharedTableChoosePackage', {}, $t('sharedTableChoosePackage')))"
        >
          <button
            v-for="quantity in sharedAllowedQuantities"
            :key="quantity"
            type="button"
            class="shared-table-package"
            :class="{ 'shared-table-package--selected': ticketQuantity === quantity }"
            :aria-checked="ticketQuantity === quantity"
            role="radio"
            @click="ticketQuantity = quantity"
          >
            <span class="shared-table-package__quantity">{{ workflowLabel(quantity) }}</span>
            <span class="shared-table-package__label">
              {{ workflowLabel(quantity === 1 ? workflowCustomerLabel('sharedTableTicketSingular', {}, $t('sharedTableTicketSingular')) : workflowCustomerLabel('sharedTableTicketPlural', {}, $t('sharedTableTicketPlural'))) }}
            </span>
            <v-icon
              v-if="ticketQuantity === quantity"
              class="shared-table-package__check"
              :size="18"
              color="#FFFFFF"
            >
              check_circle
            </v-icon>
          </button>
        </div>
        <div v-else class="top-buttons">
          <button
            type="button"
            class="quantity-button"
            :aria-label="adminText('workflowsAll.gratis_decrease', 'Decrease quantity')"
            :class="{ 'quantity-button--disabled': ticketQuantity === 0 }"
            :disabled="ticketQuantity === 0"
            @click="decreaseTicketNum"
          >
            <v-icon :size="20" color="#FFFFFF">remove</v-icon>
          </button>
          <span class="ticket-number">{{ workflowLabel(ticketQuantity) }}</span>
          <button
            type="button"
            class="quantity-button"
            :aria-label="adminText('workflowsAll.gratis_increase', 'Increase quantity')"
            :class="{ 'quantity-button--disabled': disableIncreaseButton }"
            :disabled="disableIncreaseButton"
            @click="increaseTicketNum"
          >
            <v-icon :size="20" color="#FFFFFF">add</v-icon>
          </button>
        </div>
      </div>

      <p v-if="numberOfNeededTickets > 0" class="remaining-tickets">
        {{ adminText('workflowsAll.odaberi_jos_min_0096fdc', '*Odaberi još min.') }} <b>{{ workflowLabel(numberOfNeededTickets) }} {{ adminText('workflowsAll.ulaznica_be99fe0', 'ulaznica') }}</b>
      </p>

      <div class="bottom-buttons">
        <button
          type="button"
          class="action-button action-button--secondary"
          @click="handleCancelAction"
        >
          {{ workflowLabel(showAddedState ? workflowCustomerLabel('close', {}, $t('close')) : workflowCustomerLabel('cancel', {}, $t('cancel'))) }}
        </button>
        <div v-if="showAddedState" class="added-state">
          <v-icon :size="20" color="#55A35D">check_circle</v-icon>
          <span class="added-state__text">{{ workflowLabel(workflowCustomerLabel('addedToCart', {}, $t('addedToCart'))) }}</span>
        </div>
        <button
          v-else
          type="button"
          class="action-button action-button--primary"
          :disabled="
            ticketQuantity === 0 ||
              numberOfNeededTickets > 0 ||
              ticketQuantity > getMaxAllowedQuantity() ||
              (isSharedTable && !sharedAllowedQuantities.includes(ticketQuantity))
          "
          @click="handleSubmit"
        >
          {{ workflowLabel(orderItem.itemMeta.parter && !ticketQuantity ? adminText('workflowsAll.izbrisi_0cadab4', 'Izbriši') : workflowCustomerLabel('add', {}, $t('add'))) }}
        </button>
      </div>

      <v-snackbar v-model="snackbarOpen" color="primary" :timeout="2500" bottom right>
        {{ workflowLabel(snackbarMessage) }}
      </v-snackbar>
    </div>
  </v-dialog>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { isSharedTable, getSharedTableQuantities } from '@/utils/floor-plan-quantity'

export default {
  mixins: [WorkflowDisplay],
  name: 'floor-plan-element-dialog',
  props: {
    openDialog: {
      type: Boolean,
      default: false,
    },
    orderItem: {
      type: Object,
      default: () => {},
    },
    initialQuantity: {
      type: Number,
      default: 1,
    },
    quantityLimit: {
      type: Number,
      default: 10,
    },
    itemQuantityLimit: {
      type: Number,
      default: null,
    },
    currentSelectedAmount: {
      type: Number,
      default: 0,
    },
    existingQuantity: {
      type: Number,
      default: null,
    },
    availableTableQuantity: {
      type: Number,
      default: null,
    },
    showCartState: {
      type: Boolean,
      default: true,
    },
    isInitiallySelected: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      ticketQuantity: this.initialQuantity,
      snackbarOpen: false,
      snackbarMessage: '',
      descriptionExpanded: false,
      autoCloseTimer: null,
    }
  },
  computed: {
    formattedPrice() {
      return `${Number(this.orderItem.price).toFixed(2)} ${this.orderItem.currency}`
    },
    helperText() {
      if (this.isSharedTable) {
        return this.sharedAllowedQuantities.length
          ? this.workflowCustomerLabel('sharedTableChoosePackage', {}, this.$t('sharedTableChoosePackage'))
          : this.workflowCustomerLabel('sharedTableNoAvailablePackages', {}, this.$t('sharedTableNoAvailablePackages'))
      }
      const min = this.orderItem.itemMeta.min || 0
      const max = this.orderItem.itemMeta.max || 10

      if (min === 0) {
        return this.workflowCustomerLabel('floorPlanHelperMaxOnly', { max }, this.$t('floorPlanHelperMaxOnly', { max }))
      }

      return this.workflowCustomerLabel('floorPlanHelperRange', { min, max }, this.$t('floorPlanHelperRange', { min, max }))
    },
    hasExistingSelection() {
      return this.isInitiallySelected
    },
    showAddedState() {
      return (
        this.showCartState && this.hasExistingSelection &&
        this.ticketQuantity === this.initialQuantity &&
        this.numberOfNeededTickets <= 0
      )
    },
    numberOfNeededTickets() {
      if (this.isSharedTable) {
        return 0
      }

      return (this.orderItem.itemMeta.min || 0) - this.ticketQuantity
    },
    isSharedTable() {
      return isSharedTable(this.orderItem)
    },
    sharedAllowedQuantities() {
      if (!this.isSharedTable) return []

      const available = this.availableTableQuantity === null
        ? this.getMaxAllowedQuantity()
        : this.availableTableQuantity
      const availableQuantities = getSharedTableQuantities(this.orderItem, available, this.getMaxAllowedQuantity())
      const configured = this.orderItem.itemMeta.sharedTable.allowedQuantities || []
      // An existing checkout can already hold its current package.
      if (this.showCartState && this.hasExistingSelection &&
          configured.includes(this.ticketQuantity) && !availableQuantities.includes(this.ticketQuantity)) {
        return [this.ticketQuantity, ...availableQuantities]
      }

      return availableQuantities
    },
    disableIncreaseButton() {
      const nextRawQuantity = this.ticketQuantity + 1

      return this.exceedsQuantityLimit(nextRawQuantity)
    },
    showDescriptionToggle() {
      return (this.orderItem.description || '').trim().length > 0
    },
  },
  watch: {
    openDialog(isOpen) {
      if (isOpen) {
        this.ticketQuantity = this.initialQuantity
        this.normalizeSharedTableQuantity()
      } else {
        this.descriptionExpanded = false
      }
    },
    initialQuantity(nextQuantity) {
      this.ticketQuantity = nextQuantity
      this.normalizeSharedTableQuantity()
    },
    sharedAllowedQuantities() {
      this.normalizeSharedTableQuantity()
    },
    'orderItem.description'() {
      this.descriptionExpanded = false
    },
    showAddedState(isAdded) {
      clearTimeout(this.autoCloseTimer)
      if (isAdded) {
        this.autoCloseTimer = setTimeout(() => {
          this.$emit('closeDialog')
        }, 1000)
      }
    },
  },
  mounted() {
    this.normalizeSharedTableQuantity()
  },
  beforeDestroy() {
    clearTimeout(this.autoCloseTimer)
  },
  methods: {
    normalizeSharedTableQuantity() {
      if (this.isSharedTable && !this.sharedAllowedQuantities.includes(this.ticketQuantity)) {
        this.ticketQuantity = this.sharedAllowedQuantities[0] || 0
      }
    },
    toggleDescription() {
      this.descriptionExpanded = !this.descriptionExpanded
    },
    showBundleGratisAddedSnackbar(count) {
      if (!count || count <= 0) {
        return
      }

      this.snackbarMessage = { workflowCustomer: 'bundleOfferGratisAdded', params: { count } }
      this.snackbarOpen = true
    },
    getMaxAllowedQuantity() {
      const remainingOrderCapacity = Math.max(
        this.quantityLimit - (this.currentSelectedAmount - (this.hasExistingSelection ? (this.existingQuantity === null ? this.initialQuantity : this.existingQuantity) : 0)),
        0,
      )
      const maxByParentItem =
        this.itemQuantityLimit === null ? Number.MAX_SAFE_INTEGER : this.itemQuantityLimit

      if (this.orderItem.itemMeta.parter) {
        return Math.min(
          parseInt(this.orderItem.quantity),
          remainingOrderCapacity,
          maxByParentItem,
          this.orderItem.itemMeta && this.orderItem.itemMeta.max
            ? this.orderItem.itemMeta.max
            : Number.MAX_SAFE_INTEGER,
        )
      }

      return Math.min(
        remainingOrderCapacity,
        maxByParentItem,
        this.orderItem.itemMeta && this.orderItem.itemMeta.max
          ? this.orderItem.itemMeta.max
          : Number.MAX_SAFE_INTEGER,
      )
    },
    getBundleAdjustedQuantity(quantity, direction = 'up') {
      const bundleOffer = this.orderItem && this.orderItem.bundleOffer
      if (!bundleOffer || bundleOffer.availableQuantity <= 0 || quantity <= 0) {
        return quantity
      }

      const quantityInterval = Number(bundleOffer.quantityInterval)
      const freeQuantity = Number(bundleOffer.freeQuantity)
      const availableQuantity = Number(bundleOffer.availableQuantity)
      const bundleSize = quantityInterval + freeQuantity

      if (!quantityInterval || !freeQuantity || !bundleSize || !availableQuantity) {
        return quantity
      }

      const maxBundleCoveredQuantity = bundleSize * availableQuantity
      if (quantity > maxBundleCoveredQuantity) {
        return quantity
      }

      const remainder = quantity % bundleSize
      if (remainder < quantityInterval || remainder === 0) {
        return quantity
      }

      if (direction === 'down') {
        return quantity - (remainder - (quantityInterval - 1))
      }

      return quantity + (bundleSize - remainder)
    },
    exceedsQuantityLimit(candidateQuantity) {
      const normalizedQuantity = this.getBundleAdjustedQuantity(candidateQuantity, 'up')
      const maxAllowedQuantity = this.getMaxAllowedQuantity()

      return candidateQuantity > maxAllowedQuantity && normalizedQuantity > maxAllowedQuantity
    },
    increaseTicketNum() {
      const candidateQuantity = this.ticketQuantity + 1
      const normalizedQuantity = this.getBundleAdjustedQuantity(candidateQuantity, 'up')
      const maxAllowedQuantity = this.getMaxAllowedQuantity()

      if (candidateQuantity > maxAllowedQuantity) {
        return
      }

      const finalQuantity =
        normalizedQuantity <= maxAllowedQuantity ? normalizedQuantity : candidateQuantity
      this.ticketQuantity = finalQuantity
      this.showBundleGratisAddedSnackbar(finalQuantity - candidateQuantity)
    },
    decreaseTicketNum() {
      const candidateQuantity = Math.max(this.ticketQuantity - 1, 0)

      this.ticketQuantity = this.getBundleAdjustedQuantity(candidateQuantity, 'down')
    },
    handleCancelAction() {
      if (this.showAddedState) {
        this.$emit('closeDialog')

        return
      }

      this.$emit('removeTicketFromOrder')
    },
    handleSubmit() {
      if (
        this.ticketQuantity === 0 || this.numberOfNeededTickets > 0 ||
        this.ticketQuantity > this.getMaxAllowedQuantity() ||
        (this.isSharedTable && !this.sharedAllowedQuantities.includes(this.ticketQuantity))
      ) {
        return
      }

      this.$emit('addTickets', this.ticketQuantity)
    },
  },
}
</script>

<style scoped>
p {
  color: #333333;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5;
  margin: 0;
}

.content-container {
  background: #f5f5f5;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
}

.card-header {
  align-items: center;
  display: flex;
  gap: 8px;
}

.ticket-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.price {
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  font-size: 16px;
  color: #333;
  text-transform: uppercase;
  letter-spacing: -0.48px;
  -webkit-font-feature-settings: 'lnum', 'pnum';
  font-feature-settings: 'lnum', 'pnum';
  font-variant-numeric: lining-nums proportional-nums;
  white-space: nowrap;
}

.helper-text {
  color: #333333;
}

.description-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.description {
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.description--expanded {
  overflow: visible;
  text-overflow: initial;
  white-space: normal;
  word-break: break-word;
}

.description-toggle {
  align-items: center;
  align-self: flex-start;
  background: none;
  border: none;
  color: #333333;
  cursor: pointer;
  display: inline-flex;
  gap: 4px;
  padding: 0;
}

.description-toggle span {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.18px;
  line-height: 1.2;
  text-transform: uppercase;
}

.ticket-container {
  align-items: center;
  background: #ffffff;
  border-radius: 32px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 16px 24px 16px 20px;
}

.ticket-container--shared {
  align-items: stretch;
  border-radius: 16px;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
}

.ticket-container--shared-single {
  align-items: center;
  border-radius: 32px;
  flex-direction: row;
  padding: 12px 16px 12px 20px;
}

.ticket-container--shared-single .shared-table-packages {
  margin-left: auto;
}

.ticket-container__price {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.6px;
  line-height: 1.2;
  text-transform: uppercase;
}

.top-buttons {
  align-items: center;
  display: flex;
  gap: 16px;
}

.shared-table-packages {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fit, minmax(82px, 1fr));
}

.shared-table-packages--single {
  display: flex;
  justify-content: center;
}

.shared-table-packages--single .shared-table-package {
  flex: 0 0 148px;
  min-height: 58px;
  max-width: 148px;
}

.shared-table-package {
  align-items: center;
  background: #f7fafc;
  border: 1px solid #cfdde6;
  border-radius: 12px;
  color: #265b7c;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  font-weight: 600;
  justify-content: center;
  min-height: 76px;
  padding: 10px 8px;
  position: relative;
  transition: background-color 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease,
    transform 0.16s ease;
}

.shared-table-package:hover {
  border-color: #0277bd;
  box-shadow: 0 4px 10px rgba(2, 119, 189, 0.12);
  transform: translateY(-1px);
}

.shared-table-package:focus-visible {
  outline: 3px solid rgba(2, 119, 189, 0.3);
  outline-offset: 2px;
}

.shared-table-package__quantity {
  color: inherit;
  font-family: 'Raleway', sans-serif;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.72px;
  line-height: 1;
}

.shared-table-package__label {
  color: inherit;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.18px;
  line-height: 1.2;
  margin-top: 6px;
  text-transform: uppercase;
}

.shared-table-package__check {
  position: absolute;
  right: 6px;
  top: 6px;
}

.shared-table-package--selected {
  background: #0277bd;
  border-color: #0277bd;
  box-shadow: 0 4px 12px rgba(2, 119, 189, 0.25);
  color: #ffffff;
}

.shared-table-package--selected:hover {
  border-color: #0277bd;
}

.quantity-button {
  align-items: center;
  background: #333333;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  display: flex;
  height: 30px;
  justify-content: center;
  transition: opacity 0.2s ease;
  width: 30px;
}

.quantity-button--disabled,
.quantity-button:disabled {
  cursor: default;
  opacity: 0.3;
}

.ticket-number {
  color: #333333;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: -0.72px;
  line-height: 1.2;
  min-width: 24px;
  text-align: center;
  user-select: none;
}

.remaining-tickets {
  color: #666666;
  font-size: 14px;
}

.bottom-buttons {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

.added-state {
  align-items: center;
  display: flex;
  gap: 8px;
  min-height: 24px;
}

.added-state__text {
  color: #333333;
  font-size: 16px;
  line-height: 1.5;
}

.action-button {
  background: transparent;
  border: none;
  border-radius: 24px;
  cursor: pointer;
  font-family: 'Raleway', sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.24px;
  line-height: 1.2;
  padding: 6px 12px;
  text-transform: uppercase;
}

.action-button--secondary {
  color: #333333;
}

.action-button--primary {
  background: #333333;
  color: #ffffff;
}

.action-button--primary:disabled {
  cursor: default;
  opacity: 0.4;
}

@media screen and (max-width: 600px) {
  .ticket-name { white-space: normal; overflow-wrap: anywhere; }
  .ticket-container { flex-wrap: wrap; padding: 12px; }
  .top-buttons { margin-left: auto; gap: 10px; }
  .quantity-button { width: 44px; height: 44px; }
  .action-button { min-height: 44px; }
  .shared-table-packages { min-width: 0; }
}
</style>

<style>
.floor-plan-element-dialog {
  background: transparent !important;
  box-shadow: none !important;
  overflow: visible !important;
}
</style>
